import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useScrollLoadingContext } from '../../../Contexts/ScrollLoadingContext';
import { useProfileContext } from '../../../Contexts/ProfileContext';
import { useCustomToast } from '../../../Contexts/ToastContext';

import { useDispatch, useSelector } from 'react-redux';
import {
  setDisableBtn,
  toggleDialog,
  setRerender,
  setRerenderSubscription,
  toggleLoader,
  setShowPaymentPlans,
} from '../../../../redux/Slices/Global/globalSlice';
import { useMutation } from 'react-query';
import { createCheckoutSession, createStripeToken, processPayment } from '../../../../queries';

/**
 * For "addScreens" action: keeps the legacy card-entry form (prorated charge).
 * For all other actions (new subscription / plan change): redirects to Stripe Checkout.
 */
function PaymentForm({ closeDialog }) {
  const { t } = useTranslation();
  const dispatch     = useDispatch();
  const disableBtn   = useSelector((state) => state.global.disableBtn);
  const { paymentData, selectedPackage, selectedPricing, numberOfScreens, action } = useProfileContext();
  const { ids }      = useScrollLoadingContext();
  const { showToast } = useCustomToast();

  // ── Stripe Checkout redirect ────────────────────────────────────────────────

  const checkoutMutation = useMutation(createCheckoutSession, {
    onSuccess: ({ checkout_url }) => {
      window.location.href = checkout_url;
    },
    onError: (error) => {
      showToast('error', t('payment.toast_checkout'), error?.response?.data?.message ?? t('payment.checkout_error'));
      dispatch(setDisableBtn(false));
    },
  });

  const handleCheckoutRedirect = () => {
    dispatch(setDisableBtn(true));
    checkoutMutation.mutate({
      packageId:       selectedPackage?.id,
      period:          selectedPricing?.period,
      numberOfDevices: numberOfScreens,
      devicesList:     ids,
    });
  };

  // ── Legacy add-screens flow (charge-based) ──────────────────────────────────

  const [cardData, setCardData] = useState({
    card_number: '',
    expiry_date: '',
    cvv: '',
  });

  const stripeMutation = useMutation(createStripeToken, {
    onSuccess: (token) => {
      paymentMutation.mutate({
        token,
        packageData: { selectedPackage, selectedPricing, numberOfScreens, ids, action },
      });
    },
    onError: (error) => {
      showToast('error', t('payment.toast_package_payment'), error?.response?.data?.error?.message);
      dispatch(setDisableBtn(false));
    },
  });

  const paymentMutation = useMutation(processPayment, {
    onSuccess: (result) => {
      dispatch(setRerenderSubscription(true));
      dispatch(setDisableBtn(false));
      dispatch(toggleDialog('paymentDialog'));
      dispatch(toggleLoader('reload'));
      dispatch(setRerender(true));
      dispatch(setShowPaymentPlans(false));
      showToast('success', t('payment.toast_package_payment'), result?.data?.msg);
    },
    onError: () => {
      showToast('error', t('payment.toast_package_payment'), t('payment.payment_failed'));
      dispatch(setDisableBtn(false));
    },
  });

  const handleLegacySubmit = (e) => {
    e.preventDefault();
    dispatch(setDisableBtn(true));
    stripeMutation.mutate(cardData);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (action !== 'addScreens') {
    return (
      <div className="mt-3 d-flex flex-column align-items-center gap-3">
        <p className="text-center fs-8 text-muted mb-0">
          {t('payment.stripe_redirect_notice')}
        </p>
        <div className="col-12 d-flex justify-content-center mt-2">
          <button
            disabled={disableBtn}
            type="button"
            className="btn btn-label-secondary me-3 px-4 border"
            onClick={closeDialog}
          >
            {t('buttons.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary px-4"
            disabled={disableBtn}
            onClick={handleCheckoutRedirect}
          >
            {disableBtn ? (
              <Spinner variant="white" as="span" animation="border" size="sm" role="status" className="mx-3" />
            ) : (
              <>
                {t('payment.proceed_to_checkout')}&nbsp;<b>$ {paymentData.total}</b>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Legacy card form for add-screens
  return (
    <div className="mt-3">
      <form onSubmit={handleLegacySubmit} className="d-flex flex-column align-items-center">
        <div className="col-12">
          <div className="form-group mb-2">
            <label className="fs-8">{t('payment.card_number')}</label>
            <input
              type="text"
              className="w-100 form-control"
              placeholder="**** **** **** ****"
              value={cardData.card_number}
              onChange={(e) => setCardData((p) => ({ ...p, card_number: e.target.value }))}
              required
            />
          </div>
        </div>
        <div className="col-12 d-flex flex-column flex-sm-row">
          <div className="form-group mb-2 pe-sm-1 col-12 col-sm-6">
            <label className="fs-8">{t('payment.expiry_label')}</label>
            <input
              type="text"
              className="w-100 form-control"
              placeholder="MM/YY"
              value={cardData.expiry_date}
              onChange={(e) => setCardData((p) => ({ ...p, expiry_date: e.target.value }))}
              required
            />
          </div>
          <div className="form-group mb-2 col-12 col-sm-6">
            <label className="fs-8">{t('payment.cvv_label')}</label>
            <input
              type="text"
              className="w-100 form-control"
              placeholder="***"
              value={cardData.cvv}
              onChange={(e) => setCardData((p) => ({ ...p, cvv: e.target.value }))}
              required
            />
          </div>
        </div>
        <div className="col-12 d-flex justify-content-center mt-5">
          <button
            disabled={disableBtn}
            type="reset"
            className="btn btn-label-secondary me-3 px-4 border"
            onClick={closeDialog}
          >
            {t('buttons.cancel')}
          </button>
          <button type="submit" className="btn btn-primary px-4" disabled={disableBtn}>
            {disableBtn ? (
              <Spinner variant="white" as="span" animation="border" size="sm" role="status" className="mx-3" />
            ) : (
              <>{t('payment.pay_button')}&nbsp;<b>$ {paymentData.total}</b></>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PaymentForm;
